import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopyOutlined,
  CheckOutlined,
} from '@mui/icons-material';
import { agentApi } from '@/services/api';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

function statusChipProps(status: ConnectionStatus): {
  label: string;
  color: 'success' | 'error' | 'default';
} {
  switch (status) {
    case 'connected':
      return { label: 'Connected', color: 'success' };
    case 'disconnected':
      return { label: 'Disconnected', color: 'error' };
    case 'error':
      return { label: 'Error', color: 'error' };
    case 'connecting':
    default:
      return { label: 'Connecting…', color: 'default' };
  }
}

export default function TaskConsolePage() {
  const { taskId } = useParams<{ taskId: string }>();

  const [logToken, setLogToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [copied, setCopied] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch log_token via authenticated API — keeps token out of the page URL
  useEffect(() => {
    if (!taskId) return;
    agentApi
      .getTaskLogToken(taskId)
      .then(({ log_token }) => setLogToken(log_token))
      .catch(() => setTokenError('Could not load task console — task not found or access denied'));
  }, [taskId]);

  // Auto-scroll to bottom when new lines arrive
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines]);

  // SSE connection — opens only after log_token is fetched
  useEffect(() => {
    if (!taskId || !logToken) return;

    const url = `/api/v1/tasks/${encodeURIComponent(taskId)}/log-stream?token=${encodeURIComponent(logToken)}`;
    const es = new EventSource(url);

    setStatus('connecting');

    es.onopen = () => {
      setStatus('connected');
    };

    es.onmessage = (event: MessageEvent) => {
      setStatus('connected');
      setLines((prev) => [...prev, event.data as string]);
    };

    es.addEventListener('task_init', () => {
      setStatus('connected');
    });

    es.addEventListener('task_done', () => {
      setStatus('disconnected');
      es.close();
    });

    es.onerror = () => {
      setStatus((prev) => (prev === 'connecting' ? 'error' : 'disconnected'));
      es.close();
    };

    return () => {
      es.close();
    };
  }, [taskId, logToken]);

  const handleCopy = async () => {
    if (lines.length === 0) return;
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API may be unavailable in some contexts — silently ignore
    }
  };

  if (!taskId) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>Missing task ID</Alert>
      </Box>
    );
  }

  if (tokenError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>{tokenError}</Alert>
      </Box>
    );
  }

  if (!logToken) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ mr: 2 }} />
        <Typography>Loading console…</Typography>
      </Box>
    );
  }

  const { label, color } = statusChipProps(status);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#121212',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#8899aa', fontFamily: 'monospace', fontSize: '0.8rem' }}
          >
            Task: {taskId}
          </Typography>
          <Chip label={label} color={color} size="small" sx={{ fontWeight: 600 }} />
        </Box>

        <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
          <span>
            <IconButton
              size="small"
              onClick={handleCopy}
              disabled={lines.length === 0}
              sx={{ color: copied ? 'success.main' : '#8899aa' }}
              aria-label="Copy console output"
            >
              {copied ? (
                <CheckOutlined fontSize="small" />
              ) : (
                <ContentCopyOutlined fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Terminal box */}
      <Paper
        elevation={0}
        ref={scrollRef}
        sx={{
          flex: 1,
          backgroundColor: '#1e1e1e',
          borderRadius: 2,
          p: 2,
          maxHeight: '80vh',
          overflowY: 'auto',
          border: '1px solid #2a2a2a',
        }}
      >
        {lines.length === 0 && status === 'connecting' && (
          <Typography
            component="span"
            sx={{
              color: '#5c7a8a',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            Waiting for output…
          </Typography>
        )}

        {lines.length === 0 && (status === 'disconnected' || status === 'error') && (
          <Typography
            component="span"
            sx={{
              color: '#e05c5c',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            {status === 'error'
              ? 'Connection failed. The task may not exist or the token is invalid.'
              : 'Stream ended with no output.'}
          </Typography>
        )}

        {lines.map((line, idx) => (
          <Box
            key={idx}
            component="pre"
            sx={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#d4d4d4',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {line}
          </Box>
        ))}

        {lines.length > 0 && (status === 'disconnected' || status === 'error') && (
          <Box
            component="pre"
            sx={{
              margin: 0,
              mt: 1,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: '#e05c5c',
              lineHeight: 1.6,
            }}
          >
            --- stream ended ---
          </Box>
        )}
      </Paper>
    </Box>
  );
}
